import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = authed, false = anon
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ currency: "USD", currency_symbol: "$" });

  const loadSettings = useCallback(async () => {
    try { const { data } = await api.get("/settings"); setSettings(data); } catch { /* keep default */ }
  }, []);

  const money = useCallback((n) => {
    const num = Number(n || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: settings.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    } catch {
      return `${settings.currency_symbol} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }, [settings]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadSettings();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    loadSettings();
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth, formatApiError, settings, setSettings, money }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
